// ─── suggestGlobalSynthesis.js ─────────────────────────────────────────────
// Aide à la rédaction pour Anissa (praticienne) : à partir des analyses
// qu'ELLE a saisies (valeurs labo + ses lectures cliniques + statuts), l'IA
// PROPOSE un BROUILLON de synthèse clinique globale orientée pistes
// NUTRITIONNELLES. Anissa relit, édite et valide. L'IA propose, Anissa décide.
//
// GARDE-FOUS (stricts) :
// - Périmètre NUTRITIONNEL uniquement (alimentation, micronutrition, hygiène de
//   vie). JAMAIS un diagnostic médical ni une interprétation médicale.
// - Consolide UNIQUEMENT ce qu'Anissa a saisi. N'invente aucune valeur, ne
//   déduit aucun résultat non fourni.
// - Défaut conservateur : formulations prudentes ("pistes à envisager", "à
//   confirmer cliniquement"), jamais d'affirmation péremptoire.
// - Sortie = BROUILLON pré-rempli dans un textarea. Jamais appliqué ni
//   sauvegardé automatiquement.
// - Aucune décision clinique codée en dur ici : le prompt cadre le périmètre.
//
// Réutilise le client Claude unifié (services/anthropic.js — jamais d'appel
// direct à /api/claude).

import { callClaude, ClaudeApiError } from './anthropic';

// Libellés lisibles des statuts cliniques (miroir de STATUSES dans
// ClientJourneyPage.jsx). Sert uniquement à rendre le statut saisi par Anissa
// lisible dans le prompt — aucune logique clinique ici.
const STATUS_LABELS = {
  optimal: 'Optimal',
  surveiller: 'À surveiller',
  sous_optimal: 'Sous-optimal',
  eleve: 'Élevé',
  non_interpretable: 'Non interprétable',
};

const SYSTEM_PROMPT = `Tu aides Anissa à rédiger un BROUILLON de synthèse à partir des analyses qu'ELLE a déjà saisies et interprétées (Anissa n'est ni médecin ni biologiste). Ton rôle : mettre au propre SA lecture dans un langage de nutritionniste, simple et concret.

REGISTRE — LANGAGE NUTRITIONNISTE SIMPLE (le point le plus important)
- Écris comme une nutritionniste parle, pas comme un compte-rendu de laboratoire ou un médecin.
- Traduis toujours en langage FONCTIONNEL et concret : ce que la personne RESSENT (fatigue en après-midi, digestion lente, ballonnements, transit paresseux) et ce qu'on va faire dans l'ASSIETTE et le quotidien.
- Phrases courtes et accessibles. Une cliente doit pouvoir comprendre.

INTERDIT — JARGON MÉDICAL / BIOLOGIQUE
- N'emploie PAS de termes techniques de labo : noms de bactéries (ex. Firmicutes, Bacteroidetes, Akkermansia), ratios, "courbe cortisolémique", "profil hormonal", "en bas de norme", noms de marqueurs chiffrés.
- SEULE EXCEPTION : si Anissa a elle-même écrit un de ces termes dans sa lecture, tu peux le reprendre tel quel. Sinon, tu l'exprimes en mots simples (ex. "équilibre de la flore intestinale à soutenir", "énergie qui retombe l'après-midi").
- Tu ne poses JAMAIS de diagnostic, tu ne nommes aucune maladie, aucun traitement ni dosage médicamenteux. La décision clinique appartient à Anissa seule.

CE QUE TU AS LE DROIT DE FAIRE
- Consolider et relier UNIQUEMENT ce qu'Anissa a fourni (valeurs, ses lectures, statuts).
- Dégager 2 ou 3 priorités nutritionnelles concrètes qui ressortent de CE qu'elle a écrit.

INTERDICTIONS ABSOLUES
- N'invente AUCUNE valeur, AUCUN marqueur, AUCUN résultat absent des données fournies. Si une donnée manque, tu ne la supposes pas.
- Ne recopie pas ligne à ligne : tu SYNTHÉTISES.

TON (défaut prudent)
- Formulations prudentes : "piste à envisager", "on va plutôt soutenir", "semble aller vers". Jamais de certitude sur une cause.
- Vocabulaire d'accompagnement : "soutenir", "favoriser", "prioriser", "rééquilibrer". Évite "soigne", "guérit", "traite", "détecte".

FORMAT
- Français, ~3 à 6 lignes, texte suivi ou courtes puces. Concis et actionnable.
- Réponds UNIQUEMENT avec le texte, sans titre, sans préambule ("Voici…"), sans markdown, sans guillemets englobants.

RAPPEL : c'est un BROUILLON qu'Anissa relit, ajuste et valide. Reste simple, sobre et fidèle à ce qu'elle a saisi.`;

/**
 * Construit le message utilisateur (les analyses saisies) envoyé à l'IA.
 * Pure : ne dépend d'aucun I/O. Testable isolément.
 *
 * @param {object} analyses
 * @param {Array} [analyses.fromPlan]  - analyses prescrites : { test_name, test_code, value, synthesis, category, status, markers[] }
 * @param {Array} [analyses.external]  - analyses externes : { name, value, synthesis, category, status }
 * @returns {string} le prompt utilisateur
 */
export function buildGlobalSynthesisPrompt(analyses = {}) {
  const fromPlan = Array.isArray(analyses.fromPlan) ? analyses.fromPlan : [];
  const external = Array.isArray(analyses.external) ? analyses.external : [];

  const lines = [];

  const renderStatus = (status) => (status ? STATUS_LABELS[status] || status : null);

  const renderEntry = (label, value, synthesis, category, status, markers) => {
    const block = [`[${label}]`];
    if (category) block.push(`Catégorie : ${category}`);
    if (status) block.push(`Statut clinique (Anissa) : ${renderStatus(status)}`);
    if (value) block.push(`Valeurs saisies : ${value}`);
    if (synthesis) block.push(`Lecture clinique (Anissa) : ${synthesis}`);
    if (Array.isArray(markers) && markers.length) {
      const filled = markers.filter((m) => m && (m.value || m.synthesis));
      filled.forEach((m) => {
        const parts = [`  · ${m.label || m.marker_code}`];
        if (m.value) parts.push(`= ${m.value}${m.unit ? ` ${m.unit}` : ''}`);
        if (m.status) parts.push(`(${renderStatus(m.status)})`);
        block.push(parts.join(' '));
        if (m.synthesis) block.push(`    Note : ${m.synthesis}`);
      });
    }
    return block.join('\n');
  };

  const prescribed = fromPlan.filter(
    (r) =>
      r &&
      (r.value ||
        r.synthesis ||
        (Array.isArray(r.markers) && r.markers.some((m) => m && (m.value || m.synthesis)))),
  );
  if (prescribed.length) {
    lines.push('=== ANALYSES PRESCRITES (saisies par Anissa) ===');
    prescribed.forEach((r) => {
      lines.push(
        renderEntry(
          r.test_name || r.test_code || 'Analyse',
          r.value,
          r.synthesis,
          r.category,
          r.status,
          r.markers,
        ),
      );
      lines.push('');
    });
  }

  // V97.125 — Ne plus exiger un titre (r.name) : une analyse externe où Anissa
  // a saisi des valeurs (ou une lecture) DOIT compter même sans titre — sinon
  // ses données sont silencieusement ignorées par la synthèse. Libellé de repli
  // neutre « Analyse externe » (aucune interprétation clinique ajoutée).
  const ext = external.filter((r) => r && (r.value || r.synthesis));
  if (ext.length) {
    lines.push('=== ANALYSES EXTERNES (déjà détenues par la cliente) ===');
    ext.forEach((r) => {
      lines.push(renderEntry(r.name || 'Analyse externe', r.value, r.synthesis, r.category, r.status));
      lines.push('');
    });
  }

  lines.push('=== TÂCHE ===');
  lines.push(
    "Rédige un BROUILLON de synthèse (~3-6 lignes), en langage SIMPLE de nutritionniste (pas de jargon de labo, pas de noms de marqueurs sauf si Anissa les a écrits), qui consolide UNIQUEMENT ces éléments et dégage 2-3 priorités nutritionnelles concrètes (ressenti + assiette + quotidien). N'invente rien. Reste prudente et fidèle à ce qui est saisi.",
  );

  return lines.join('\n');
}

/**
 * Indique s'il y a assez de matière saisie pour proposer une synthèse.
 * Utilisé par l'UI pour désactiver le bouton / afficher un message.
 *
 * @param {object} analyses - { fromPlan[], external[] }
 * @returns {boolean}
 */
export function hasEnoughAnalysesForSynthesis(analyses = {}) {
  const fromPlan = Array.isArray(analyses.fromPlan) ? analyses.fromPlan : [];
  const external = Array.isArray(analyses.external) ? analyses.external : [];
  const hasContent = (r) =>
    r &&
    (r.value ||
      r.synthesis ||
      (Array.isArray(r.markers) && r.markers.some((m) => m && (m.value || m.synthesis))));
  // V97.125 — cohérent avec buildGlobalSynthesisPrompt : une analyse externe
  // avec des valeurs/lecture compte même sans titre (sinon bouton désactivé à
  // tort alors qu'Anissa a bien saisi des données).
  return fromPlan.some(hasContent) || external.some((r) => r && hasContent(r));
}

/**
 * Appelle Claude pour proposer un BROUILLON de synthèse clinique globale.
 * Ne sauvegarde rien : renvoie juste le texte proposé, que l'UI pré-remplit
 * dans le textarea qu'Anissa édite ensuite.
 *
 * @param {object} analyses - { fromPlan[], external[] }
 * @returns {Promise<string>} le brouillon de synthèse
 * @throws {Error} message clair si pas de matière / erreur IA / réponse vide
 */
export async function suggestGlobalSynthesis(analyses = {}) {
  if (!hasEnoughAnalysesForSynthesis(analyses)) {
    throw new Error(
      "Saisis d'abord au moins une analyse (valeur ou lecture clinique) avant de demander une proposition.",
    );
  }

  const user = buildGlobalSynthesisPrompt(analyses);

  let text;
  try {
    text = await callClaude({
      system: SYSTEM_PROMPT,
      user,
      maxTokens: 800,
      // Filet de sécurité compliance en plus du guard vocabulaire injecté par défaut.
      sanitizeOutput: true,
    });
  } catch (err) {
    if (err instanceof ClaudeApiError) {
      throw new Error(`La proposition a échoué : ${err.message}`);
    }
    throw new Error(`La proposition a échoué : ${err?.message || err}`);
  }

  const draft = (text || '').trim();
  if (!draft) {
    throw new Error("La préparation n’a renvoyé aucune proposition. Réessaie.");
  }
  return draft;
}
